import type { OnboardingPrefill } from "@shared/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getMyOnboardingPrefill } from "@/features/roles/services/roles-api.service";
import { queryKeys } from "@/lib/query-keys";
import type { LocationValue } from "../components/fields";

/**
 * Details the account already gave, for the onboarding of a role it is
 * adding. Requested only while `enabled`, which the pages set when the
 * account holds another role; a first sign-up never asks. Undefined until it
 * arrives, and when the request fails: the form then starts empty.
 */
export function useOnboardingPrefill(
	enabled: boolean,
): OnboardingPrefill | undefined {
	const role = useAuthStore((state) => state.user?.userType ?? null);
	const { data } = useQuery({
		queryKey: queryKeys.user.onboardingPrefill(role),
		queryFn: getMyOnboardingPrefill,
		enabled,
	});
	return data;
}

/**
 * Calls `apply` once, the first time `prefill` is set. The flows' `apply`
 * fills only fields that are still empty, so nothing the user typed before
 * the prefill arrived is replaced, and later edits stay theirs.
 */
export function useApplyPrefillOnce(
	prefill: OnboardingPrefill | undefined,
	apply: (prefill: OnboardingPrefill) => void,
): void {
	const applied = useRef(false);
	const latestApply = useRef(apply);
	latestApply.current = apply;
	useEffect(() => {
		if (!prefill || applied.current) return;
		applied.current = true;
		latestApply.current(prefill);
	}, [prefill]);
}

/** `current` when it holds anything, else the prefilled value or "". */
export const keepOrFill = (
	current: string,
	prefilled: string | null,
): string => (current.trim() ? current : (prefilled ?? ""));

/**
 * `current` when the user has entered any of city, state or pincode, else
 * all three from the prefill. All or none, so a city the user typed is never
 * paired with another place's state and pincode.
 */
export function fillLocationIfEmpty(
	current: LocationValue,
	known: OnboardingPrefill,
): LocationValue {
	if (current.city.trim() || current.state || current.pincode.trim()) {
		return current;
	}
	return {
		city: known.city ?? "",
		state: known.state ?? "",
		pincode: known.pincode ?? "",
	};
}
